// @ts-check
import {track} from 'common/report/util/settings-tracking';
import {appApiFetchJson, djangoFetch} from 'common/report/queries/utils';
import {RequestType} from 'common/report/queries/public-api/compliance-client';
import {toWorkspaceObj} from 'common/report/queries/app-api/workspaces-client';

/** @typedef { import('../../public-api/compliance-client').ComplianceJob } ComplianceJob */
/** @typedef { import('./org-types').AddProjectsToTeamsResponse } AddProjectsToTeamsResponse */
/** @typedef { import('./org-types').AddProjectsToTeamTeamDescriptor } AddProjectsToTeamTeamDescriptor */
/** @typedef { import('./org-types').AddUsersToProjectsResponse } AddUsersToProjectsResponse */
/** @typedef { import('./org-types').AddUsersToTeamsResponse } AddUsersToTeamsResponse */
/** @typedef { import('./org-types').DeleteResponse } DeleteResponse */
/** @typedef { import('./org-types').DeleteUserResponse } DeleteUserResponse */
/** @typedef { import('./org-types').DetailedUser } DetailedUser */
/** @typedef { import('./org-types').DomainClaimResponse } DomainClaimResponse */
/** @typedef { import('./org-types').DomainGetResponse } DomainGetResponse */
/** @typedef { import('./org-types').DomainVerifyResponse } DomainVerifyResponse */
/** @typedef { import('./org-types').DomainWithdrawClaimResponse } DomainWithdrawClaimResponse */
/** @typedef { import('./org-types').IdMergeStatusResponse } IdMergeStatusResponse */
/** @typedef { import('./org-types').Project } OrganizationsPayload */
/** @typedef { import('./org-types').Project } Project */
/** @typedef { import('./org-types').ProjectGrantCreationResponse } ProjectGrantCreationResponse */
/** @typedef { import('./org-types').ProjectGrantDescriptor } ProjectGrantDescriptor */
/** @typedef { import('./org-types').ProjectPermissionsPayload } ProjectPermissionsPayload */
/** @typedef { import('./org-types').ProjectRolesPayload } ProjectRolesPayload */
/** @typedef { import('./org-types').ProjectUserGrantDescriptor } ProjectUserGrantDescriptor */
/** @typedef { import('./org-types').RoleDescriptor } RoleDescriptor */
/** @typedef { import('./org-types').Sso } Sso */
/** @typedef { import('./org-types').Team } Team */
/** @typedef { import('./org-types').TeamMembershipDescriptor } TeamMembershipDescriptor */
/** @typedef { import('./org-types').Team } TransferProjectResponse */
/** @typedef { import('./org-types').SessionSettings } SessionSettings */
/** @typedef { import('./org-types').UndeleteResponse } UndeleteResponse */
/** @typedef { import('./org-types').UpdateDefaultClusterIdResponse} UpdateDefaultClusterIdResponse*/
/** @typedef { import('./org-types').UpdateOrgTeamRequest } UpdateOrgTeamRequest */
/** @typedef { import('./org-types').UpdateOrgUserRequest } UpdateOrgUserRequest */
/** @typedef { import('./org-types').UpdateRequest } UpdateRequest */
/** @typedef { import('./org-types').UpdateResponse } UpdateResponse */
/** @typedef { import('./org-types').UserInvite } UserInvite */
/** @typedef { import('./org-types').UserInviteResponse } UserInviteResponse */
/**
 * app api endpoint for organizations
 * @param {number} organizationId
 * @return {string}
 */
export function orgsUrl(organizationId = 0) {
  return `/organizations/${organizationId || ``}`;
}

/**
 * app api endpoint for organization teams
 * @param {number} organizationId
 * @param {number} teamId
 * @return {string}
 */
export function teamUrl(organizationId, teamId = 0) {
  const teamString = teamId || ``;
  return `/organizations/${organizationId}/teams/${teamString}`;
}

/**
 * app api endpoint for organization users
 * @param {number} organizationId
 * @param {string} email
 * @return {string}
 */
export function userUrl(organizationId, email = ``) {
  return `/organizations/${organizationId}/users/${encodeURIComponent(email)}`;
}

/**
 * POST /organizations/<organizationId>/update
 * @param {number} organizationId: ID of org to update
 * @param {UpdateRequest} attrs: parameters to pass
 * @returns {Promise<UpdateResponse>} response
 */
export async function updateOrganization(organizationId, attrs) {
  return appApiFetchJson(`${orgsUrl(organizationId)}/update/`, {
    method: `post`,
    body: JSON.stringify(attrs),
  });
}

/**
 * Fetch data about the organization's projects
 * @param {number} organizationId - org ID
 * @param {object} kwargs
 * @param {boolean} [kwargs.includeSessionSettings] - boolean whether or not to include session settings in the payload
 * @param {boolean} [kwargs.includeTimePeriodSettings] - boolean whether or not to include time period settings in the payload
 * @param {boolean} [kwargs.includeProjectFeatureflags] - boolean whether or not to include featureflags in the payload
 * @returns {Promise<Project[]>} array of projects
 */
export async function fetchOrgProjectData(
  organizationId,
  {includeSessionSettings = false, includeTimePeriodSettings = false, includeProjectFeatureflags = false} = {},
) {
  return appApiFetchJson(
    `${orgsUrl(organizationId)}/projects/`,
    {method: `get`},
    {
      include_session_settings: includeSessionSettings,
      include_time_period_settings: includeTimePeriodSettings,
      include_project_featureflags: includeProjectFeatureflags,
    },
  );
}

/**
 * Fetch Compliance Retrieval Jobs for an entire Organization
 * @param {number} organizationId
 * @returns {Promise<ComplianceJob[]>} complianceJobs
 */
export async function fetchOrgComplianceRetrievalJobs(organizationId) {
  const results = await appApiFetchJson(`${orgsUrl(organizationId)}/data-retrievals/`, {method: `get`});
  return results.map((cj) => ({
    requestTaskId: cj.tracking_id,
    status: cj.status,
    destinationUrl: cj.destination_url,
    projectId: cj.project_id,
    distinctIdCount: cj.distinct_id_count,
    dateRequested: cj.date_requested,
    requestingUser: cj.requesting_user,
    compliance: cj.compliance_type,
    requestType: RequestType.DATA_EXPORT,
    disclosureType: cj.disclosure_type,
  }));
}

/**
 * Fetch User Deletion Compliance Jobs for an entire Organization
 * @param {number} organizationId
 * @returns {Promise<ComplianceJob[]>} complianceJobs
 */
export async function fetchOrgComplianceDeletionJobs(organizationId) {
  const results = await appApiFetchJson(`${orgsUrl(organizationId)}/data-deletions/`, {method: `get`});
  return results.map((cj) => ({
    requestTaskId: cj.tracking_id,
    status: cj.status,
    destinationUrl: cj.destination_url,
    projectId: cj.project_id,
    distinctIdCount: cj.distinct_id_count,
    dateRequested: cj.date_requested,
    compliance: cj.compliance_type,
    requestType: RequestType.USER_DELETION,
  }));
}

/**
 * Fetch Compliance Jobs for an entire Organization
 * @param {number} organizationId
 * @returns {Promise<ComplianceJob[]>} complianceJobs
 */
export async function fetchOrgComplianceJobs(organizationId) {
  const [retrievals, deletions] = await Promise.all([
    fetchOrgComplianceRetrievalJobs(organizationId),
    fetchOrgComplianceDeletionJobs(organizationId),
  ]);

  return retrievals.concat(deletions);
}

/**
 * Delete a subset of the organization's projects
 * @param {number} organizationId - org ID
 * @param {number[]} projectIds - array of project IDs to delete
 * @returns {Promise<DeleteResponse>} array of deleted project IDs
 */
export async function deleteOrgProjects(organizationId, projectIds) {
  const url = `${orgsUrl(organizationId)}/delete-projects/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({projectIds}),
  });
}

/**
 * Undelete a subset of the organization's projects
 * @param {number} organizationId - org ID
 * @param {number[]} projectIds - array of project IDs to undelete
 * @returns {Promise<UndeleteResponse>} array of undeleted project IDs
 */
export async function undeleteOrgProjects(organizationId, projectIds) {
  const url = `${orgsUrl(organizationId)}/undelete-projects/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({projectIds}),
  });
}

/**
 * Create projects under organization
 * @param {number} organizationId - org ID
 * @param {string[]} projectNames - array of project names to create
 * @returns {Promise<Project[]>} array of created projects
 */
export async function createOrgProjects(organizationId, projectNames) {
  const url = `${orgsUrl(organizationId)}/create-projects/`;
  const reponsePromise = appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({projectNames}),
  });

  // Queue up a track call
  reponsePromise.then((response) => {
    response.forEach((project) =>
      track(`Created New Project`, {
        'Project Id': project.id,
        'Project Name': project.name,
      }),
    );
  });
  return reponsePromise;
}

/**
 * Fetch data about the organization's teams
 * @param {number} organizationId - org ID
 * @returns {Promise<Team[]>} array of Team descriptors
 */
export async function fetchOrgTeamData(organizationId) {
  return appApiFetchJson(`${orgsUrl(organizationId)}/teams/`, {
    method: `get`,
  });
}

export async function fetchOrgWorkspaceData(organizationId) {
  const resp = await appApiFetchJson(`${orgsUrl(organizationId)}/workspaces/`, {
    method: `get`,
  });
  return resp.map(toWorkspaceObj);
}

/**
 * Delete organization teams
 * @param {number} organizationId - org ID
 * @param {number[]} teamIds - array of Team IDs to delete
 * @returns {Promise<DeleteResponse>} array of deleted team IDs
 */
export async function deleteOrgTeams(organizationId, teamIds) {
  const url = `${orgsUrl(organizationId)}/delete-teams/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({teamIds}),
  });
}

/**
 * Create organization teams
 * @param {number} organizationId - org ID
 * @param {string[]} teamNames - array of Team Names to create
 * @returns {Promise<string[]>} array of created team IDs
 */
export async function addOrgTeams(organizationId, teamNames) {
  const url = `${orgsUrl(organizationId)}/add-teams/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({teamNames}),
  });
}

/**
 * Update organizations default cluster id and project cluster id
 * @param {number} organizationId - ID of the organization to operate on
 * @param {number} projectId - ID of the project to update
 * @param {number} clusterId - the cluster id to set the default to
 * @returns {Promise<UpdateDefaultClusterIdResponse>} boolean result indicating success or failure
 */
export async function updateOrgDefaultAndProjectClusterId(organizationId, projectId, clusterId) {
  const url = `${orgsUrl(organizationId)}/update-org-project-cluster/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({cluster_id: clusterId, project_id: projectId}),
  });
}

/**
 *
 * @param {number} organizationId
 * @param {ProjectUserGrantDescriptor[]} projectDescriptors
 * @returns {Promise<AddUsersToProjectsResponse>}
 */
export async function addUsersToOrgProjects(organizationId, projectDescriptors) {
  const url = `${orgsUrl(organizationId)}/add-users-to-projects/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({projects: projectDescriptors}),
  });
}

/**
 *
 * @param {number} organizationId
 * @param {ProjectUserGrantDescriptor} projectDescriptor
 * @param {boolean} isInviteRequestAcceptance
 * @returns {Promise<AddUsersToProjectsResponse>}
 */
export async function addUsersToOrgProject(organizationId, projectDescriptor, isInviteRequestAcceptance = false) {
  const url = `${orgsUrl(organizationId)}/add-users-to-project/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({
      isInviteRequestAcceptance,
      ...projectDescriptor,
    }),
  });
}

/**
 * Add users to Teams
 * @param {number} organizationId - org ID
 * @param {TeamMembershipDescriptor[]} teamDescriptors - array of user emails to add
 * @returns {Promise<AddUsersToTeamsResponse>} array of added members
 */
export async function addUsersToOrgTeams(organizationId, teamDescriptors) {
  const url = `${orgsUrl(organizationId)}/add-users-to-teams/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({teams: teamDescriptors}),
  });
}

/**
 *
 * @param {number} organizationId - org ID
 * @param {AddProjectsToTeamTeamDescriptor[]} teamDescriptors - teams w/ embedded project descriptors
 * @returns {Promise<AddProjectsToTeamsResponse>}
 */
export async function addProjectsToOrgTeams(organizationId, teamDescriptors) {
  const url = `${orgsUrl(organizationId)}/add-projects-to-teams/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({teams: teamDescriptors}),
  });
}

/**
 * Remove users from Team
 * @param {number} organizationId - org ID
 * @param {number} teamId - team ID
 * @param {string[]} userEmails - emails of users to remove from team
 * @returns {Promise<DeleteUserResponse>} array of deleted user emails
 */
export async function removeUsersFromOrgTeam(organizationId, teamId, userEmails) {
  const url = `${teamUrl(organizationId, teamId)}/delete-users/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({userEmails}),
  });
}

/**
 * Remove projects from Team
 * @param {number} organizationId - org ID
 * @param {number} teamId - team ID
 * @param {number[]} projectIds - array of project ids to add
 * @returns {Promise<DeleteResponse>} array of deleted project ids
 */
export async function removeProjectsFromOrgTeam(organizationId, teamId, projectIds) {
  const url = `${teamUrl(organizationId, teamId)}/delete-projects/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({projectIds}),
  });
}

/**
 * Resend existing invites and extend their expiration date
 * @param {number} orgId - organization ID
 * @param {string[]} emails - array of email addresses to resend invites to
 * @returns {Promise<>} object with email keys and result of re-send
 */
export async function resendOrgInvites(orgId, emails) {
  const url = `${orgsUrl(orgId)}/resend-invites/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify(emails),
  });
}

/**
 * Fetch data about the organization's users
 * @param {number} organizationId - org ID
 * @returns {Promise<DetailedUser[]>} array of objects
 */
export async function fetchOrgUserData(organizationId) {
  return appApiFetchJson(`${orgsUrl(organizationId)}/users/`, {
    method: `get`,
  });
}

/**
 * POST: Delete the organization's users
 * @param {number} organizationId - org ID
 * @param {string[]} userEmails - array of emails for users to delete
 * @returns {Promise<DeleteResponse>} payload with count of deleted users
 */
export async function deleteOrgUsers(organizationId, userEmails) {
  const url = `${orgsUrl(organizationId)}/delete-users/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({userEmails}),
  });
}

/**
 * POST: Add the organization's users
 * @param {number} organizationId - org ID
 * @param {UserInvite[]} users - array of user invite objects to create
 * @returns {Promise<UserInviteResponse[]>} array of objects
 */
export async function addOrgUsers(organizationId, users) {
  const url = `${orgsUrl(organizationId)}/add-users/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({users}),
  });
}

/**
 * POST: Add the organization's users using csv file
 * @param {number} organizationId - org ID
 * @param {File} attachment - csv file include users to add
 * @returns {Promise<UserInviteResponse[]>} array of objects
 */
export async function addOrgUsersWithAttachment(organizationId, attachment) {
  const url = `${orgsUrl(organizationId)}/bulk-add-users/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: attachment,
  });
}

/**
 * POST: Update an organization's team
 * @param {number} organizationId - org ID
 * @param {number} teamId - team ID
 * @param {UpdateOrgTeamRequest} request - request with params to update
 * @returns {Promise<UpdateResponse>} array of objects
 */
export async function updateOrgTeam(organizationId, teamId, request) {
  const url = `${teamUrl(organizationId, teamId)}/update/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify(request),
  });
}

/**
 * POST: Update an organization's user
 * @param {number} organizationId - org ID
 * @param {string} email - email
 * @param {UpdateOrgUserRequest} request - params to update
 * @returns {Promise<UpdateResponse>} promise with response
 */
export async function updateOrgUser(organizationId, email, request) {
  const url = `${userUrl(organizationId, email)}/update/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify(request),
  });
}

/**
 *
 * @param {number} organizationId - organization ID
 * @returns {Promise<Sso>}
 */
export async function fetchOrgSsoData(organizationId) {
  const url = `${orgsUrl(organizationId)}/sso/`;
  return appApiFetchJson(url, {
    method: `get`,
  });
}

/**
 *
 * @param {number} organizationId - organization ID
 * @returns {Promise<boolean>} active flag of 2FA
 */
export async function fetchOrg2faData(organizationId) {
  const url = `${orgsUrl(organizationId)}/twofactor/`;
  return appApiFetchJson(url, {
    method: `get`,
  });
}

/**
 *
 * @param {number} organizationId - organization ID
 * @param {boolean} active - value to set twofactor_auth_required to
 * @param {string} secret - secret to confirm password
 * @returns {Promise<boolean>} new value
 */
export async function updateOrg2faData(organizationId, active, secret) {
  const url = `${orgsUrl(organizationId)}/twofactor/update/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({active, secret}),
  });
}

/**
 *
 * @param {number} organizationId - organization ID
 * @returns {Promise<ProjectPermissionsPayload>} - map of projectId => list of permissions
 */
export async function getOrgProjectPermissions(organizationId) {
  const url = `${orgsUrl(organizationId)}/project-permissions`;
  return appApiFetchJson(url, {method: `get`});
}

/**
 *
 * @param {number} organizationId - organization ID
 * @returns {Promise<ProjectRolesPayload>} - map of projectId => list of roles
 */
export async function getOrgAssignableProjectRoles(organizationId) {
  const url = `${orgsUrl(organizationId)}/assignable-project-roles`;
  return appApiFetchJson(url, {method: `get`});
}

/**
 *
 * @param {number} organizationId - organization ID
 * @param {boolean} forServiceAccounts - whether or not to fetch org roles for service accounts
 * @returns {Promise<RoleDescriptor[]>} - list of roles
 */
export async function getOrgAssignableOrgRoles(organizationId, forServiceAccounts = false) {
  const url = `${orgsUrl(organizationId)}/assignable-org-roles`;
  return appApiFetchJson(url, {method: `get`}, {force_all: forServiceAccounts});
}

/**
 *
 * @param {number} organizationId - organization ID
 * @returns {Promise<RoleDescriptor[]>} - list of roles
 */
export async function getOrgEditableOrgRoles(organizationId) {
  const url = `${orgsUrl(organizationId)}/assignable-org-roles`;
  return appApiFetchJson(url, {method: `get`}, {force_all: true});
}

/**
 *
 * @returns {Promise<OrganizationsPayload>} - obj w/ org info
 */
export async function getOrgs() {
  const url = `${orgsUrl()}`;
  return appApiFetchJson(url, {method: `get`});
}

/**
 *
 * @param targetOrgId {number} org ID to transfer to
 * @param projectId {number} project ID of project being transferred
 * @returns {Promise<*>}
 */
export async function transferProject(targetOrgId, projectId) {
  const url = `${orgsUrl(targetOrgId)}/transfer-project/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({project_id: projectId}),
  });
}

/**
 * Deletion using the project endpoint
 * @param projectId {number} project ID to delete
 * @returns {Promise<*>}
 */
export async function deleteProject(projectId) {
  const url = `/projects/delete/${projectId}/`;
  return djangoFetch(url);
}

/**
 * Undelete using the project endpoint
 * @param projectId {number} project ID to undelete
 * @returns {Promise<*>}
 */
export async function undeleteProject(projectId) {
  const url = `/projects/undelete/${projectId}/`;
  return djangoFetch(url);
}

/**
 *
 * @param orgId {number} org ID to leave
 * @returns {Promise<DeleteUserResponse>} list of (one) email removed
 */
export async function leaveOrg(orgId) {
  const url = `${orgsUrl(orgId)}/leave/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({}),
  });
}

/**
 *
 * @returns {Promise<*>}
 */
export async function deleteUserAccount() {
  const url = `/account/delete/`;
  return appApiFetchJson(url, {method: `post`});
}

/**
 * @param {number} orgId associated with invitation
 * @param {string[]} emails - list of emails to regenerate invites for
 * @returns {Promise<*>} obj with email keys and relative invite url values
 */
export async function regenerateOrgInviteUrls(orgId, emails) {
  const url = `${orgsUrl(orgId)}/regenerate-invite-url/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify(emails),
  });
}

/**
 * app api endpoint for domain claiming
 * @return {string}
 */
export function domainClaimUrl() {
  return `/security/domains/`;
}

/**
 *
 * @param {number} orgId - ID of org to get domains for
 * @returns {Promise<DomainGetResponse[]>}
 */
export function fetchDomainData(orgId) {
  return appApiFetchJson(`${domainClaimUrl()}${orgId}/`, {method: `GET`, body: null});
}

/**
 *
 * @param {number} orgId - ID of org to verify domain for
 * @param {string} domain - domain to verify claim for
 * @returns {Promise<DomainVerifyResponse>}
 */
export function verifyDomain(orgId, domain) {
  return appApiFetchJson(`${domainClaimUrl()}verify/${orgId}/`, {
    method: `post`,
    body: JSON.stringify({domain}),
  });
}

/**
 *
 * @param {number} orgId - ID of org to claim a domain for
 * @param {string} domain - domain for which to remove claim
 * @param {string} secret - secret to confirm password
 * @returns {Promise<DomainClaimResponse>}
 */
export function claimDomain(orgId, domain, secret) {
  return appApiFetchJson(`${domainClaimUrl()}claim/${orgId}/`, {
    method: `post`,
    body: JSON.stringify({domain, secret}),
  });
}

/**
 *
 * @param {number} orgId - ID of org to withdraw a claim for
 * @param {string} domain - domain for which to remove claim
 * @param {string} secret - secret to confirm password
 * @returns {Promise<DomainWithdrawClaimResponse>}
 */
export function withdrawDomainClaim(orgId, domain, secret) {
  return appApiFetchJson(`${domainClaimUrl()}withdraw-claim/${orgId}/`, {
    method: `post`,
    body: JSON.stringify({domain, secret}),
  });
}

/**
 *
 * @param {number} orgId - ID of org to enable id merge for
 * @returns {Promise<*>}
 */
export function enableIdMerge(orgId) {
  return appApiFetchJson(`${orgsUrl(orgId)}/enable-id-merge/`, {
    method: `post`,
  });
}

/**
 *
 * @param {number} orgId - ID of org to get id merge status for
 * @returns {Promise<IdMergeStatusResponse>}
 */
export function getIdMergeStatus(orgId) {
  return appApiFetchJson(`${orgsUrl(orgId)}/get-id-merge-status/`, {
    method: `get`,
  });
}
